// =============================================================================
// PrintButton.jsx — print icon button + printChild helper
// =============================================================================
// PrintButton: a small button that triggers a print action.
// printChild:  FUNCTION (not a component) that builds and opens a printable
//              HTML window for one child's approved uniform/items order.
// Props (PrintButton):
//   onClick  - fired when clicked
//   disabled - if true, render greyed out and ignore clicks
// Params (printChild — one options object):
//   child, childIndex, shelterName, refNo, isSL, shelterCode, shelterRef,
//   reqDate, totalChildren
// =============================================================================

import { customUniformRowCounts, UNIFORM_ITEMS, WINTER_ITEMS } from "../../utils/formConstants";
import { money } from "../../utils/helpers";
import { escapeHtml } from "../../utils/sanitize";

function PrintButton({ onClick, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? "Print available after request is approved" : "Print order sheet"}
      style={{
        background: "none",
        border: `1px solid ${disabled ? "#CFD8DC" : "#00897B"}`,
        color: disabled ? "#CFD8DC" : "#00897B",
        borderRadius: 6,
        padding: "5px 14px",
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      Print
    </button>
  );
}

function printChild({
  child,
  childIndex,
  shelterName,
  refNo,
  isSL,
  shelterCode,
  shelterRef,
  reqDate,
  totalChildren,
}) {
  const items = child.items || {};
  const allItems = [].concat(UNIFORM_ITEMS, WINTER_ITEMS);
  const pageNum = childIndex + 1;
  const pageTotal = totalChildren || 1;

  let fmtReqDate = "";
  if (reqDate) {
    const d = new Date(reqDate);
    if (!Number.isNaN(d.getTime())) {
      fmtReqDate =
        d.getDate().toString().padStart(2, "0") +
        "/" +
        (d.getMonth() + 1).toString().padStart(2, "0") +
        "/" +
        d.getFullYear();
    } else {
      fmtReqDate = String(reqDate).slice(0, 10);
    }
  }

  const itemRows = allItems
    .map(function (item) {
      const d = items[item] || {};
      const qty = parseInt(d.qty, 10) || 0;
      const cost = parseFloat(d.cost) || 0;
      const sub = qty * cost;
      if (qty === 0) return "";
      const skuCols = isSL
        ? "<td style='padding:7px 10px'>" +
          escapeHtml(d.sku) +
          "</td><td style='padding:7px 10px'>" +
          escapeHtml(d.size) +
          "</td>"
        : "";
      return (
        "<tr>" +
        "<td style='padding:7px 10px'>" +
        escapeHtml(item) +
        "</td>" +
        skuCols +
        "<td style='padding:7px 10px;text-align:right'>" +
        (cost > 0 ? money(cost) : "") +
        "</td>" +
        "<td style='padding:7px 10px;text-align:center'>" +
        qty +
        "</td>" +
        "<td style='padding:7px 10px;text-align:right'>" +
        (sub > 0 ? money(sub) : "-") +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  const customRows = (child.customItems || [])
    .map(function (it) {
      const qty = parseInt(it.qty, 10) || 0;
      const cost = parseFloat(it.cost) || 0;
      const sub = qty * cost;
      const label = it.name || it.desc || "";
      // Only real line items (identity + qty + cost) appear on the order —
      // keeps the printed rows and the printed total in step.
      if (!customUniformRowCounts(it)) return "";
      const skuCols = isSL
        ? "<td style='padding:7px 10px'>" +
          escapeHtml(it.sku) +
          "</td><td style='padding:7px 10px'>" +
          escapeHtml(it.size) +
          "</td>"
        : "";
      return (
        "<tr>" +
        "<td style='padding:7px 10px'>" +
        escapeHtml(label || "Custom item") +
        "</td>" +
        skuCols +
        "<td style='padding:7px 10px;text-align:right'>" +
        (cost > 0 ? money(cost) : "") +
        "</td>" +
        "<td style='padding:7px 10px;text-align:center'>" +
        (qty || "") +
        "</td>" +
        "<td style='padding:7px 10px;text-align:right'>" +
        (sub > 0 ? money(sub) : "-") +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  const total =
    allItems.reduce(function (s, k) {
      const d = items[k] || {};
      return s + (parseInt(d.qty, 10) || 0) * (parseFloat(d.cost) || 0);
    }, 0) +
    (child.customItems || []).reduce(function (s, it) {
      if (!customUniformRowCounts(it)) return s;
      return s + (parseInt(it.qty, 10) || 0) * (parseFloat(it.cost) || 0);
    }, 0);

  const headerRow = function (label, value) {
    return (
      "<tr>" +
      "<td style='padding:5px 10px;font-weight:600;width:220px;background:#f0f0f0;border:1px solid #ccc'>" +
      label +
      "</td>" +
      "<td style='padding:5px 10px;border:1px solid #ccc'>" +
      escapeHtml(value) +
      "</td>" +
      "</tr>"
    );
  };

  const genderLabel = child.gender
    ? child.gender.charAt(0).toUpperCase() + child.gender.slice(1)
    : "";
  const yearLabel = child.yearLevel || "";
  const schoolLabel = child.schoolName || "";
  const orgRef = shelterRef || refNo || "";
  const notifEmail = child.email || "";
  const notifSms = child.mobile || "";

  const header =
    "<table style='border-collapse:collapse;width:100%;margin-bottom:20px'>" +
    headerRow("Request Date", fmtReqDate) +
    headerRow("DFV Service/Org. Code", shelterCode || "") +
    headerRow("DFV Service/Org. Name", shelterName || "") +
    headerRow("DFV Service/Org. Reference", orgRef) +
    headerRow("DFV Service/Org. Notification Email", notifEmail) +
    headerRow("DFV Service/Org. Notification SMS", notifSms) +
    headerRow("Zephyr Reference No.", refNo || "") +
    (isSL && child.osdCode ? headerRow("School Locker Reference", child.osdCode) : "") +
    "</table>";

  const colHeaders =
    "<th style='padding:8px 10px;background:#005a87;color:#fff;text-align:left'>UNIFORM Item</th>" +
    (isSL
      ? "<th style='padding:8px 10px;background:#005a87;color:#fff;text-align:left'>SKU</th><th style='padding:8px 10px;background:#005a87;color:#fff;text-align:left'>Size</th>"
      : "") +
    "<th style='padding:8px 10px;background:#005a87;color:#fff;text-align:right'>Cost</th>" +
    "<th style='padding:8px 10px;background:#005a87;color:#fff;text-align:center'>Qty</th>" +
    "<th style='padding:8px 10px;background:#005a87;color:#fff;text-align:right'>$ Total</th>";

  const itemsTable =
    "<table style='border-collapse:collapse;width:100%;margin-bottom:16px'>" +
    "<thead><tr>" +
    colHeaders +
    "</tr></thead>" +
    "<tbody>" +
    itemRows +
    customRows +
    "</tbody>" +
    "<tfoot><tr>" +
    (isSL
      ? "<td colspan='4' style='padding:8px 10px;font-weight:700;border-top:2px solid #005a87'>TOTAL COST</td>"
      : "<td colspan='2' style='padding:8px 10px;font-weight:700;border-top:2px solid #005a87'>TOTAL COST</td>") +
    "<td style='padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #005a87'>" +
    money(total) +
    "</td>" +
    "</tr></tfoot>" +
    "</table>";

  const pageStyle = "page-break-after:always";
  const childHeading =
    "<h2 style='margin:0 0 16px 0;font-size:16px;color:#005a87'>" +
    (isSL ? "School Locker Purchase - Zephyr Education" : "Uniform Order - Zephyr Education") +
    "</h2>" +
    "<h3 style='margin:0 0 12px 0;font-size:14px;color:#333'>Child " +
    pageNum +
    " &nbsp;<span style='font-size:12px;font-weight:normal;color:#666'>Gender: " +
    escapeHtml(genderLabel) +
    " &nbsp;|&nbsp; Year Level: " +
    escapeHtml(yearLabel) +
    " &nbsp;|&nbsp; School: " +
    escapeHtml(schoolLabel) +
    "</span></h3>";

  const pageFooter =
    "<p style='text-align:right;font-size:11px;color:#666;margin-top:8px'>Page " +
    pageNum +
    " of " +
    pageTotal +
    "</p>";

  const pageHtml = `<div style='${pageStyle}'>${childHeading}${header}${itemsTable}${pageFooter}</div>`;

  const html =
    "<!DOCTYPE html><html><head><meta charset='UTF-8'>" +
    "<title>School Locker Order - " +
    escapeHtml(refNo) +
    "</title>" +
    "<style>" +
    "body{font-family:Arial,sans-serif;font-size:13px;color:#222;margin:0;padding:24px}" +
    "table{border-collapse:collapse}" +
    "@media print{.no-print{display:none!important}}" +
    "</style>" +
    "</head><body>" +
    pageHtml +
    "<div class='no-print' style='margin-top:24px;text-align:center'>" +
    "<button onclick='window.print()' style='padding:10px 28px;font-size:14px;background:#005a87;color:#fff;border:none;border-radius:6px;cursor:pointer'>Print / Save as PDF</button>" +
    "</div>" +
    "</body></html>";

  // No window features → opens as a new browser TAB, not a popup window.
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
}

export { PrintButton, printChild };
